import type { CheckoutPayload } from '../types';
import { cartService } from './cartService';
import { defaultAuthConfig } from '../types/auth';
import { API_BASE_URL } from '../config/api';

interface OrderServiceConfig {
  baseUrl: string;
  proxyBaseUrl: string;
  tokenKey: string;
  userKey: string;
}

const defaultOrderServiceConfig: OrderServiceConfig = {
  baseUrl: API_BASE_URL,
  proxyBaseUrl: API_BASE_URL,
  tokenKey: defaultAuthConfig.tokenKey,
  userKey: defaultAuthConfig.userKey,
};

class OrderService {
  private baseUrl: string;
  private proxyBaseUrl: string;
  private tokenKey: string;
  private userKey: string;

  constructor(config = defaultOrderServiceConfig) {
    this.baseUrl = config.baseUrl;
    this.proxyBaseUrl = config.proxyBaseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private buildHeaders(withJson = false): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (withJson) {
      headers['Content-Type'] = 'application/json';
    }

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    const rawUser = window.localStorage.getItem(this.userKey);

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (rawUser) {
      try {
        const parsedUser = this.asRecord(JSON.parse(rawUser));
        const userId = this.asString(parsedUser.id);
        const role = this.asString(parsedUser.role);
        if (userId) headers['x-user-id'] = userId;
        if (role) headers['x-user-role'] = role;
      } catch {
        // ignore malformed cache
      }
    }

    return headers;
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const record = this.asRecord(payload);
    const dataRecord = this.asRecord(record.data);
    return (
      this.asString(record.message) ||
      this.asString(dataRecord.message) ||
      this.asString(record.error) ||
      this.asString(dataRecord.error) ||
      fallback
    );
  }

  private async parseResponse(response: Response): Promise<unknown> {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const fallback = `Request failed with status ${response.status}`;
      throw new Error(this.toErrorMessage(data, fallback));
    }

    return data;
  }

  private mapCheckoutPayload(payload: CheckoutPayload): Record<string, unknown> {
    const address = payload.shippingAddress;
    const redirectUrl = payload.redirectUrl || payload.callbackUrl;
    const callbackUrl = payload.callbackUrl || redirectUrl;
    const pod = payload.podProjectId || payload.podOrderId
      ? {
          projectId: payload.podProjectId,
          orderId: payload.podOrderId,
        }
      : undefined;

    return {
      paymentMethod: payload.paymentMethod,
      shippingAddress: {
        fullName: address.fullName,
        phone: address.phone,
        street: address.street,
        addressLine1: address.street,
        addressLine2: '',
        city: address.city,
        country: address.country,
        postalCode: address.postalCode,
      },
      shippingOption: 'standard',
      pod,
      redirectUrl,
      callbackUrl,
      redirect_url: redirectUrl,
      callback_url: callbackUrl,
      metadata: {
        ...payload.metadata,
        ...(redirectUrl ? { redirectUrl } : {}),
        ...(callbackUrl ? { callbackUrl } : {}),
      },
    };
  }

  private async requestWithFallback(
    paths: string[],
    init: RequestInit,
  ): Promise<unknown> {
    let lastError: Error | null = null;

    for (const path of paths) {
      const response = await fetch(`${this.baseUrl}${path}`, init).catch((error: unknown) => {
        lastError = error instanceof Error ? error : new Error('Request failed');
        return null;
      });

      if (!response) {
        continue;
      }

      if ((response.status === 404 || response.status === 405) && paths[paths.length - 1] !== path) {
        continue;
      }

      return this.parseResponse(response);
    }

    throw lastError ?? new Error('No checkout endpoint is available.');
  }

  async createCheckout(payload: CheckoutPayload): Promise<unknown> {
    const data = await this.requestWithFallback(['/checkout', '/orders'], {
      method: 'POST',
      headers: this.buildHeaders(true),
      body: JSON.stringify(this.mapCheckoutPayload(payload)),
    });

    // Backend should clear cart; fetch latest cart to keep UI in sync.
    void cartService.getCart().catch(() => undefined);
    return data;
  }

  async checkout(payload: CheckoutPayload): Promise<unknown> {
    const data = await this.requestWithFallback(['/checkout', '/orders'], {
      method: 'POST',
      headers: this.buildHeaders(true),
      body: JSON.stringify(this.mapCheckoutPayload(payload)),
    });

    void cartService.getCart().catch(() => undefined);
    return data;
  }

  async confirmCheckout(reference: string, transactionId?: string): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}/checkout/confirm`, {
      method: 'POST',
      headers: this.buildHeaders(true),
      body: JSON.stringify({
        reference,
        ...(transactionId ? { transaction_id: transactionId } : {}),
      }),
    });
    return this.parseResponse(response);
  }

  async getOrders(): Promise<unknown> {
    const candidates = ['/orders', '/payments/my'];
    let lastError: Error | null = null;

    for (const endpoint of candidates) {
      try {
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
          method: 'GET',
          headers: this.buildHeaders(),
        });

        if (response.status === 404 || response.status === 405) {
          continue;
        }

        return await this.parseResponse(response);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(`Failed to fetch orders from ${endpoint}`);
      }
    }

    throw lastError ?? new Error('Failed to fetch orders.');
  }

  async getAdminOrders(): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}/admin/orders`, {
      method: 'GET',
      headers: this.buildHeaders(),
    });
    return this.parseResponse(response);
  }

  async getOrderById(orderId: string): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}/orders/${orderId}`, {
      method: 'GET',
      headers: this.buildHeaders(),
    });
    return this.parseResponse(response);
  }

  async getOrderStatus(orderId: string): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}/orders/${encodeURIComponent(orderId)}/status`, {
      method: 'GET',
      headers: this.buildHeaders(),
    });
    return this.parseResponse(response);
  }

  async cancelOrder(orderId: string, reason?: string): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}/orders/${encodeURIComponent(orderId)}/cancel`, {
      method: 'POST',
      headers: this.buildHeaders(true),
      body: JSON.stringify({ reason }),
    });
    return this.parseResponse(response);
  }

  async adminUpdateOrderStatus(orderId: string, payload: { status?: string; trackingNumber?: string }): Promise<unknown> {
    const encodedOrderId = encodeURIComponent(orderId);
    const normalizedPayload = {
      ...payload,
      ...(payload.status ? { orderStatus: payload.status } : {}),
    };
    const requestBaseUrl = this.proxyBaseUrl || this.baseUrl;
    const endpoints = [
      `/admin/orders/${encodedOrderId}/status`,
      `/admin/orders/${encodedOrderId}`,
    ];

    let lastError: Error | null = null;

    for (let index = 0; index < endpoints.length; index += 1) {
      const endpoint = endpoints[index];
      const hasNextEndpoint = index < endpoints.length - 1;
      const response = await fetch(`${requestBaseUrl}${endpoint}`, {
        method: 'PATCH',
        headers: this.buildHeaders(true),
        body: JSON.stringify(normalizedPayload),
      }).catch((error: unknown) => {
        lastError = error instanceof Error ? error : new Error('Request failed');
        return null;
      });

      if (!response) {
        continue;
      }

      let data: unknown = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok) {
        return data;
      }

      if (hasNextEndpoint && (response.status === 400 || response.status === 404 || response.status === 405)) {
        continue;
      }

      const fallback = `Request failed with status ${response.status}`;
      throw new Error(this.toErrorMessage(data, fallback));
    }

    throw lastError ?? new Error('Failed to update order status.');
  }

  async updateStatus(orderId: string, status: string): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}/orders/${orderId}`, {
      method: 'PATCH',
      headers: this.buildHeaders(true),
      body: JSON.stringify({ status, orderStatus: status }),
    });
    return this.parseResponse(response);
  }
}

export const orderService = new OrderService();
